import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Konva from 'konva';
import { Stage, Layer, Rect, Group } from 'react-konva';

import { shuffleArray } from '../../lib/ep';

const generatePairs = (indexed_images, matchit_distribution_mode = 'mixed', max_rotation = 20) => {
	const init = (matchit_distribution_mode == 'mixed') ? [[]] : [[], []];
	const matched = {};
	const pairs = Object.keys(indexed_images).reduce((last, id) => {
		if(['matchit_backflip1_image', 'matchit_backflip2_image', 'bg_image'].includes(id)) return last;

		const item = {
			...indexed_images[id],
			image_name:id,
			identifier:indexed_images[id].group,
			rotation:-max_rotation * Math.random() + max_rotation * Math.random(),
		};

		if(matchit_distribution_mode == 'mixed') {
			last[0].push(item);
		} else {
			let column = item?.index;
			//@deprecate this if on 2024-12-01
			if (column == undefined) {
				column = (matched[item.identifier] == undefined) ? 0 : 1;
				matched[item.identifier] = 1;
			}
			last[column].push(item);
		}

		return last;
	}, init);

	const shuffled_pairs = pairs.map((a) => shuffleArray(a));

	return shuffled_pairs;
};

const MatchCanvas = forwardRef(({ stage, ...props }, ref) => {
	const max_rotation = 20;	// in degrees

	const distribution_mode = stage.matchit_distribution_mode ?? 'separation';

	const [ full_loaded, setFullLoaded ] = useState(false);

	const backflip1 = props.indexed_images?.matchit_backflip1_image ?? false;
	const backflip2 = props.indexed_images?.matchit_backflip2_image ?? false;
	const bg_image = props.indexed_images?.bg_image ?? false;
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const refs = useRef([]);
	const size = useRef(600);
	const score = useRef(0);
	const first_element = useRef(false);
	const last_element = useRef(false);
	const items = useRef(generatePairs(props.indexed_images, distribution_mode, max_rotation));
	const is_playable = useRef(false);
	const found = useRef([]);
	const display_cards_backfliped = useRef(stage?.matchit_display_cards_backfliped ?? true);

	const columns = 5; // squared images of 1/5 of the canvas size per side
	const delay_to_success = 0.5; // in seconds

	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const card1_bg_color = stage.matchit_backflip1_bg_color ?? 'rgba(120, 195, 0, 1)';
	const card2_bg_color = stage.matchit_backflip2_bg_color ?? 'rgba(120, 195, 0, 1)';
	const card_padding_color = stage.matchit_card_padding_color ?? 'rgba(255, 255, 255, 1)';
	const use_entropy = stage?.matchit_apply_entropy ?? false;
	const card_border_width = parseInt(stage?.matchit_card_border_width ?? 2);
	const card_border_color = stage?.matchit_card_border_color ?? 'rgba(0, 0, 0, 1)';
	const card_border_padding = parseInt(stage?.matchit_card_border_padding ?? 7);
	const card_border_radius = parseInt(stage?.matchit_card_border_radius ?? 50);
	const display_outter_border = stage?.display_outter_border ?? true;

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const entropy_delta = 15;	// 10 | 15 | 20
	const missed_final_scale = 0.6;

	/*START SOUNDS*/
	const flip_sound = props?.sound_fx?.move;
	const movePlay = () => props?.sound_fx?.move.play();
	const foundPlay = () => props?.sound_fx?.found.play();
	const errorPlay = () => props?.sound_fx?.error.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();

	const moveAndSuccessPlay = () => {
		if(flip_sound === false) return;

		if(!display_cards_backfliped.current) {
			moveCardOut(first_element.current);
			moveCardOut(last_element.current);
		}

		flip_sound.once('end', () => {
			foundPlay();
		});

		flip_sound.play();
	};
	/*END SOUNDS*/

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(is_builder) return;

		window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	useEffect(() => {
		resetGame(true);
	}, [
		props.indexed_images,
	]);

	useEffect(() => {
		resetGame();
	}, [
		stage.matchit_distribution_mode,
		stage.matchit_backflip1_bg_color,
		stage.matchit_backflip2_bg_color,
		stage.matchit_card_border_padding,
		stage.matchit_card_padding_color,
		stage.matchit_card_border_width,
		stage.matchit_card_border_color,
		stage.matchit_card_border_radius,
		stage.bg_color,
	]);

	useEffect(() => {
		display_cards_backfliped.current = stage?.matchit_display_cards_backfliped ?? true;
		autoFlipCards();
	}, [ stage.matchit_display_cards_backfliped ]);

	const resetGame = (force_remount = false) => {
		if(force_remount) is_playable.current = false;

		items.current.map((item_group, i) => {
			item_group.map((foo, index) => {
				const positive_index = index + 1;
				const item_index = `${i}_${positive_index}`;

				if(refs.current[`element_${item_index}`] != undefined) {
					refs.current[`element_${item_index}`].clearCache();
				}
			});
		});

		gameInit(force_remount);
	};

	const gameInit = (force_remount = false) => {
		let apply_board_mount = !full_loaded;

		if(force_remount && items.current.length != Object.keys(props.indexed_images).length) {
			items.current = generatePairs(props.indexed_images, distribution_mode, max_rotation);
			apply_board_mount = true;
		}

		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = _size;
				setFullLoaded(true);

				setTimeout(() => {
					if(refs.current['layer'] == undefined) return;

					if(is_playable.current === false && apply_board_mount) {
						mountBoardAnimation();
					}
				}, 50);
			});
		}
	};

	const autoFlipCards = () => {
		if(refs.current['layer'] == undefined) return;

		items.current.map((item_group, i) => {
			item_group.map((foo, index) => {
				const positive_index = index + 1;
				const item_index = `${i}_${positive_index}`;

				refs.current[`backflip_${item_index}`].setAttrs({
					opacity:(display_cards_backfliped.current) ? 1 : 0,
				});

				refs.current[`piece_${item_index}`].setAttrs({
					opacity:(display_cards_backfliped.current) ? 0 : 1,
				});

				applyCache(item_index);
			});
		});

		refs.current['layer'].draw();
	};

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			is_playable.current = false;

			(success_game) ? successPlay() : failPlay();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					cb(success_game);
				}, (delay_to_success * 1000));
			});
		},
	}), []);

	const moveCardOut = (index) => {
		const item = refs.current[`element_${index}`];

		const anim_direction = (item.attrs.group == '0') ? -1 : 1;

		const animation_duration = 10;
		const displacement_end = size.current / columns / 3;
		const dist_x = anim_direction * ((item.attrs.group == '0') ? (item.attrs.x + displacement_end) : (size.current + displacement_end) - item.attrs.x) / animation_duration;
		const dist_y = ((size.current / 2) - item.attrs.y) / animation_duration;
		const dist_r = (anim_direction * 360 * 2 * Math.random()) / animation_duration;

		let i = 0;
		const anim = new Konva.Animation((frame) => {
			item.move({
				x:dist_x,
				y:dist_y,
			}).rotate(dist_r);

			i++;

			if(i >= animation_duration) anim.stop();
		}, refs.current['layer']);

		anim.start();
	};

	const flipAnimation = (index, direction = 1) => {
		const original_rotation = refs.current[`element_${index}`].attrs.rotation;
		let new_rotation = original_rotation + direction * (Math.random() * (2 * entropy_delta) - entropy_delta);
		new_rotation = Math.max(Math.min(new_rotation, 35), -35);

		refs.current[`element_${index}`].setAttrs({
			zIndex:((distribution_mode == 'separation') ? 2 : 1) * items.current[0].length,
		});

		if(use_entropy) {
			refs.current[`element_${index}`].to({
				x:refs.current[`element_${index}`].attrs.x + direction * (Math.random() * entropy_delta - 0.5 * entropy_delta),
				y:refs.current[`element_${index}`].attrs.y + direction * (Math.random() * entropy_delta - 0.5 * entropy_delta),
				rotation:new_rotation,
				duration:0.3,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`element_${index}`].to({
			scaleX:0.05,	// not 0 to display the fake "card edge"
			scaleY:1.2,
			duration:0.15,
			easing:Konva.Easings.StrongEaseIn,
			onFinish:() => {
				refs.current[`backflip_${index}`].setAttrs({
					opacity:(direction > 0) ? 0 : 1,
				});
				refs.current[`piece_${index}`].setAttrs({
					opacity:(direction > 0) ? 1 : 0,
				});

				applyCache(index);

				refs.current['layer'].draw();

				refs.current[`element_${index}`].to({
					scaleX:1,
					scaleY:1,
					duration:0.15,
					easing:Konva.Easings.StrongEaseOut,
					onFinish:() => {
						refs.current['layer'].draw();
					},
				});
			},
		});
	};

	const applyCache = (index) => {
		refs.current[`element_${index}`].cache(default_cache_props);
	};

	const highlightAnimation = (index, direction = 1, group = 0) => {
		const original_rotation = refs.current[`element_${index}`].attrs.rotation;
		let new_rotation = original_rotation + direction * (Math.random() * (2 * entropy_delta) - entropy_delta);
		new_rotation = Math.max(Math.min(new_rotation, 35), -35);

		refs.current[`element_${index}`].setAttrs({
			zIndex:2 * items.current[0].length,
		});

		let additional_props = {};
		if(use_entropy) {
			additional_props = {
				x:refs.current[`element_${index}`].attrs.x + direction * (Math.random() * entropy_delta - 0.5 * entropy_delta),
				y:refs.current[`element_${index}`].attrs.y + direction * (Math.random() * entropy_delta - 0.5 * entropy_delta),
				rotation:new_rotation,
			};
		}

		refs.current[`element_${index}`].to({
			...additional_props,
			scaleX:(direction > 0) ? 1.3 : 1,
			scaleY:(direction > 0) ? 1.3 : 1,
			duration:0.3,
			easing:Konva.Easings.BounceEaseOut,
		});
	};

	const flipCard = (index, group = 0) => {
		if(display_cards_backfliped.current) {
			flipAnimation(index, 1);
			return;
		}

		highlightAnimation(index, 1, group);
	};

	const unflipCard = (index, group = 0) => {
		if(is_playable.current === false) return;

		if(display_cards_backfliped.current) {
			flipAnimation(index, -1);
			return;
		}

		highlightAnimation(index, -1, group);
	};

	const moveCardAsideAnimation = (index) => {
		if(is_playable.current === false) return;

		const direction = (refs.current[`element_${index}`].attrs.x < size.current / 2) ? -1 : 1;

		const original_rotation = refs.current[`element_${index}`].attrs.rotation;
		let new_rotation = original_rotation + (Math.random() * (2 * entropy_delta) - entropy_delta);
		new_rotation = Math.max(Math.min(new_rotation, 35), -35);

		const x_delta = Math.random() * 20;
		const max_y_delta = size.current / 2;
		const final_x = (direction > 0) ? (size.current - x_delta) : x_delta;
		const final_y = size.current / 2 + (Math.random() * max_y_delta - 0.5 * max_y_delta);

		refs.current[`element_${index}`].to({
			x:final_x,
			y:final_y,
			rotation:new_rotation,
			scaleX:0.6,
			scaleY:0.6,
			saturation:-50,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const handleClick = (e, index, group = 0) => {
		if(is_playable.current === false) return;

		// Prevent click on an already found card:
		if(refs.current[`element_${index}`].attrs?.is_found ?? false) return;

		// Prevent click on more than 2 cards at the time:
		if(first_element.current !== false && last_element.current !== false) return;

		// Prevent double click on the same card:
		if(first_element.current !== false && first_element.current == index) return;

		// Prevent selecting cards from the same group in columned distribution mode:
		if(distribution_mode == 'separation' && first_element.current !== false) {
			if(refs.current[`element_${index}`].attrs.in_group == refs.current[`element_${first_element.current}`].attrs.in_group) return;
		}

		if(first_element.current === false) {
			first_element.current = index;
			movePlay();
		} else {
			last_element.current = index;
			checkEquals();
		}

		flipCard(index, group);
	};

	const checkEquals = () => {
		const first = refs.current[`element_${first_element.current}`].attrs;
		const last = refs.current[`element_${last_element.current}`].attrs;

		let is_equal = false;
		if(first.identifier == last.identifier) {
			found.current.push({ id:first.identifier });

			refs.current[`element_${first_element.current}`].attrs.is_found = true;
			refs.current[`element_${last_element.current}`].attrs.is_found = true;

			is_equal = true;
			moveAndSuccessPlay();
			score.current += 100;
			props.onScore(score.current);
		} else {
			movePlay();
		}

		props.doHit()

		if(found.current.length == getNumPairs()) {
			props.onEnded(true);
		}

		setTimeout(() => {
			if(!is_equal && is_playable.current == true) {
				unflipCard(first_element.current);
				unflipCard(last_element.current);

				refs.current['layer'].draw();

				errorPlay();
			}

			if(is_equal && !display_cards_backfliped.current) {
				moveCardAsideAnimation(first_element.current);
				moveCardAsideAnimation(last_element.current);
			}

			first_element.current = false;
			last_element.current = false;
		}, ((!is_equal) ? 500 : 50));
	};

	const getNumPairs = () => {
		return (distribution_mode == 'separation') ? items.current[0].length : items.current[0].length / 2;
	};

	const separateInRows = (is_final = false) => {
		const num_pairs = getNumPairs();
		if(num_pairs < 3 || num_pairs > 8) return false;

		if(distribution_mode == 'mixed' && is_final === false) {
			switch(num_pairs) {
				case 3:
					return [1, 2, 3];
	
				case 4:
					return [2, 4, 2];
	
				case 5:
					return [3, 4, 3];
	
				case 6:
					return [4, 4, 4];
	
				case 7:
					return [3, 4, 4, 3];
	
				case 8:
					return [4, 4, 4, 4];
				}
			
			return [];
		}

		switch(num_pairs) {
			case 3:
				return [1, 1, 1];

			case 4:
				return [1, 1, 1, 1];

			case 5:
				return [2, 1, 2];

			case 6:
				return [2, 2, 2];

			case 7:
				return [2, 2, 2, 1];

			case 8:
				return [2, 2, 2, 2];
		}

		return [];
	};

	const getCardInitialPositions = (is_final = false) => {
		const board = separateInRows(is_final);
		const positions = [];

		let actual_column = 0;
		let actual_row = 0;

		let usable_width = size.current;
		if(distribution_mode == 'separation' && !is_final) {
			const group_separation = size_portion * 0.6;
			usable_width = (size.current - (items.current.length - 1) * group_separation) / items.current.length;
		}

		items.current[0].map((foo) => {
			let x_delta = 0;
			let y_delta = 0;

			const A = actual_column - (board[actual_row] - 1) / 2;
			const B = actual_row - (board.length - 1) / 2;

			x_delta = A * (-0.7 * Math.abs(15 * B) + 15);
			y_delta = B * (-0.7 * Math.abs(15 * A) + 15);

			positions.push({
				x:(actual_column + 1) * (usable_width / (board[actual_row] + 1)) + x_delta,
				y:(actual_row + 1) * (size.current / (board.length + 1)) + y_delta,
			});

			actual_column++;
			if(actual_column == board[actual_row]) {
				actual_row++;
				actual_column = 0;
			}
		});

		return positions;
	};

	const getMissedCards = () => {
		const missed_cards = [];
		items.current.map((item_group, i) => {
			item_group.map((foo, index) => {
				const positive_index = index + 1;
				const item_index = `${i}_${positive_index}`;
				const is_found = refs.current[`element_${item_index}`].attrs?.is_found ?? false;
				if(!is_found) missed_cards.push(item_index);
			});
		});

		return missed_cards;
	};

	const highlightMissedCards = (is_solved, cb) => {
		if(is_solved) {
			cb();
			return;
		}

		const missed_cards = getMissedCards();

		let total = 0;
		missed_cards.map((index, i) => {
			setTimeout(() => {
				if(refs.current[`backflip_${index}`].attrs.opacity == 1) {
					// We only flip the missed cards that are unfliped
					// (some missed cards can be visible due to an
					// attempts_exceeded or time_exceeded endGame):
					flipCard(index);
				}

				setTimeout(() => {
					refs.current[`element_${index}`].to({
						scaleX:missed_final_scale,
						scaleY:missed_final_scale,
						saturation:-50,
						duration:0.4,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							total++;

							if(total == missed_cards.length) cb();
						},
					});
				}, 600);
			}, (i * 90));
		});
	};

	const mountBoardAnimation = () => {
		let finished = 0;

		items.current.map((item_group, i) => {
			item_group.map((foo, index) => {
				const positive_index = index + 1;
				const item_index = `${i}_${positive_index}`;

				const x_ini = refs.current[`element_${item_index}`].attrs.x_pos;
				const y_ini = refs.current[`element_${item_index}`].attrs.y_pos;
				const rotation = refs.current[`element_${item_index}`].attrs.angle;

				if(refs.current[`element_${item_index}`] == undefined) return;

				refs.current[`element_${item_index}`].setAttrs({
					x:size.current / 2,
					y:-(size_portion + 20),
					rotation:Math.random() * 360 - 180,
				});

				applyCache(item_index);

				setTimeout(() => {
					if(refs.current[`element_${item_index}`] == undefined) return;

					refs.current[`element_${item_index}`].to({
						x:x_ini,
						y:y_ini,
						rotation,
						duration:0.6,
						easing:Konva.Easings.StrongEaseOut,
						onFinish:() => {
							finished++;

							if(finished == items.current.length) {
								is_playable.current = true;

								if(refs.current['whole_tapper'] != undefined) {
									refs.current['whole_tapper'].setAttrs({
										y:size.current + 10,
									});
								}

								props.startTimer();
							}
						},
					});
				}, (30 * index));
			});
		});
	};

	const finalAggrupations = () => {
		const aggrupations_obj = {};
		let actual_group = 0;

		items.current.map((item_group, i) => {
			item_group.map((foo, index) => {
				const positive_index = index + 1;
				const item_index = `${i}_${positive_index}`;

				const group = refs.current[`element_${item_index}`].attrs.identifier;
				const is_found = refs.current[`element_${item_index}`].attrs?.is_found ?? false;
				if(aggrupations_obj[group] == undefined) {
					aggrupations_obj[group] = {
						index:(is_found) ? actual_group : (actual_group + items.current.length / 2),
						cards:[item_index],
					};

					actual_group++;
				} else {
					aggrupations_obj[group].cards.push(item_index);
				}
			});
		});

		const aggrupations = Object.values(aggrupations_obj);

		aggrupations.sort((a, b) => {
			if(a.index < b.index) return -1;
			return 1;
		});

		return aggrupations;
	};

	const unmountBoardAnimation = (is_solved = true, cb) => {
		refs.current['whole_tapper'].setAttrs({
			y:0,
		});

		highlightMissedCards(is_solved, () => {
			let actual_letter = 0;
			let finished = 0;
	
			const aggrupations = finalAggrupations();
			const positions = getCardInitialPositions(true);

			setTimeout(() => {
				aggrupations.map((aggrupation, a) => {
					const group_cards = aggrupation.cards;

					group_cards.map((card, i) => {
						const is_found = refs.current[`element_${card}`].attrs?.is_found ?? false;

						refs.current[`element_${card}`].setAttrs({
							zIndex:(i == 0) ? items.current[0].length : 1,
						});

						refs.current[`element_${card}`].to({
							x:positions[a].x - size_portion * 0.15 / 2 + i * size_portion * 0.15,
							y:positions[a].y - size_portion * 0.15 / 2 + i * size_portion * 0.15,
							scaleX:((is_found) ? 1 : missed_final_scale) * 0.9,
							scaleY:((is_found) ? 1 : missed_final_scale) * 0.9,
							rotation:((i > 0) ? 1 : -1) * (Math.random() * 20),
							saturation:(is_found) ? 0 : -50,
							duration:0.5,
							easing:Konva.Easings.StrongEaseInOut,
						});
					});
				});
			}, 1000);

			items.current.map((group, i) => {
				group.map((foo, index) => {
					const positive_index = index + 1;

					setTimeout(() => {
						if(refs.current[`element_${i}_${positive_index}`] == undefined) return;

						refs.current[`element_${i}_${positive_index}`].to({
							duration:0.5,
							easing:Konva.Easings.StrongEaseInOut,
							onFinish:() => {
								finished++;

								if(finished == items.current.length) {
									cb();
								}
							},
						});
					}, (actual_letter * 10));
				});
			});
		});
	};

	const img_positions = separateInRows();

	const size_multiplier = (distribution_mode == 'separation') ? (1.3 - 0.08 * (items.current[0].length - 2)) : 1;

	let size_portion = size.current / columns;
	const group_separation = size_portion * 0.6;
	const group_w = (size.current - (items.current.length - 1) * group_separation) / items.current.length;
	const group_h = size.current;
	size_portion = size_portion * size_multiplier;

	const bg_image_scale = (bg_image !== false) ? (size.current / Math.min((bg_image?.image_w ?? 600), (bg_image?.image_h ?? 600))) : 1;

	if(full_loaded === false) return null;

	const border_radius = Math.max(0, size_portion * card_border_radius / 100 / 2);
	const size_portion_bg = card_border_width + card_border_padding;
	const size_portion_prop = (size_portion + size_portion_bg + 4) / size_portion;
	const border_radius_external = border_radius * size_portion_prop;

	const card_positions = getCardInitialPositions();

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					x={0}
					y={0}
					fill={bg_color}
					width={size.current}
					height={size.current}
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="no-repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
				/>

				{items.current.map((item_group, i) => {
					var actual_column = 0;
					var actual_row = 0;

					const group_x_ini = i * (group_w + group_separation);
					const group_y_ini = 0;

					let card_fill_color = card1_bg_color;
					let backflip = backflip1;
					if(i > 0) {
						card_fill_color = card2_bg_color;
						backflip = backflip2;
					}

					let slide_size_backflip = 1;
					if(backflip !== false) slide_size_backflip = size_portion / (Math.max(backflip.image_w, backflip.image_h));

					return (
						item_group.map((value, index) => {
							actual_column++;
							if(actual_column == img_positions[actual_row]) {
								actual_row++;
								actual_column = 0;
							}

							const positive_index = index + 1;
							const item_index = `${i}_${positive_index}`;

							const is_ok = found.current.find((obj) => (obj.id == value.identifier));
							const show = (last_element.current === item_index || first_element.current === item_index || is_ok) ? true : false;
							const slide_size = size_portion / (Math.max(value.image_w, value.image_h));

							return (
								<Group
									key={`item-${item_index}`}
									ref={(node) => { refs.current[`element_${item_index}`] = node; }}
									identifier={value.identifier}
									x={size.current / 2}
									y={-(size_portion + 20)}
									x_pos={group_x_ini + card_positions[index].x}
									y_pos={group_y_ini + card_positions[index].y}
									angle={value.rotation}
									offsetX={size_portion / 2}
									offsetY={size_portion / 2}
									width={size_portion}
									height={size_portion}
									is_found={false}
									in_group={i}
									onClick={(e) => handleClick(e, item_index, i)}
									onTap={(e) => handleClick(e, item_index, i)}
									onMouseEnter={() => { document.body.style.cursor = 'pointer'; }}
									onMouseLeave={() => { document.body.style.cursor = 'default'; }}
									filters={[Konva.Filters.HSL]}
								>
									<Rect
										width={size_portion + size_portion_bg}
										height={size_portion + size_portion_bg}
										x={-size_portion_bg / 2}
										y={-size_portion_bg / 2}
										shadowBlur={size.current / 80}
										shadowColor='rgba(0, 0, 0, 0.4)'
										shadowOffsetY={size.current / 80 / 2}
										fill={card_padding_color}
										cornerRadius={border_radius_external}
										strokeWidth={card_border_width}
										stroke={card_border_color}
									/>

									<Rect
										ref={(node) => { refs.current[`backflip_${item_index}`] = node; }}
										opacity={(display_cards_backfliped.current) ? 1 : 0}
										width={size_portion}
										height={size_portion}
										cornerRadius={border_radius}
										fill={card_fill_color}
										fillPatternImage={(backflip !== false) ? backflip.fillPatternImage : null}
										//fillPatternRepeat='no-repeat' // this breaks in Firefox
										fillPatternScaleX={slide_size_backflip}
										fillPatternScaleY={slide_size_backflip}
										fillPatternOffsetX={slide_size_backflip / 2}
										fillPatternOffsetY={slide_size_backflip / 2}
										fillPriority={(backflip !== false) ? 'pattern' : 'fill'}
									/>

									<Rect
										ref={(node) => { refs.current[`piece_${item_index}`] = node; }}
										opacity={(display_cards_backfliped.current) ? 0 : 1}
										width={size_portion}
										height={size_portion}
										cornerRadius={border_radius}
										fillPatternImage={value.fillPatternImage}
										//fillPatternRepeat='no-repeat' // this breaks in Firefox
										fillPatternScaleX={slide_size}
										fillPatternScaleY={slide_size}
										fillPatternOffsetX={slide_size / 2}
										fillPatternOffsetY={slide_size / 2}
									/>
								</Group>
							)
						})
					)
				})}

				<Group
					ref={(node) => { refs.current['whole_tapper'] = node; }}
					y={0}
				>
					<Rect
						width={size.current}
						height={size.current}
						fill='white'
						opacity={0}
					/>
				</Group>
			</Layer>
		</Stage>
	);
});

export default MatchCanvas;